/* eslint-disable max-len */
/* eslint-disable id-length */
/* eslint-disable complexity */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage } from 'formik';
import {
	TextField,
	FormControlLabel,
	FormLabel,
	FormGroup,
	FormHelperText,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { injectIntl, FormattedMessage} from 'react-intl';
import { utils } from './helpers/Tab3_helpers/utils';
import ValidationSchema from './helpers/Tab3_helpers/validationSchema';
import useTab3Styles from './helpers/Tab3_helpers/tab3Styles';

import UnsupportedMedicalCases from '../../../components/modals/UnsupportedMedicalCases';
import { fireGtmEvent } from '../../../utils/marketing.utils';
import moment from 'moment';
import { getImageByAgent } from '../../../config/agent';
import { agentDeveloperUtils } from 'wobi-web-common';
import {Checkbox} from 'wobi-web-common/dist/v2/index.js';
import MedicalQuestion from '../../../components/MedicalQuestion';
import MedicalSubQuestion from '../../../components/MedicalSubQuestion';
import MedicalPageLayout from '../../../components/MedicalPageLayout';
import FlowFooter from '../../../components/FlowFooter';
import {TEST_IDS} from '../../../config/testIds';
import {scrollToFirstError} from '../../../utils/scrollToFirstError';
import './Medical.css';

const alertIcon = getImageByAgent('alert.svg');

const HAREL_MAX_DAYS = process.env.REACT_APP_HAREL_MAX_DAYS || 14;

const Tab3 = React.memo(
	({
		className: hideTabClassName,
		intl,
		idForAccessibility,
		submitCallback,
		formikIsSubmitting,
		validationCallback,
		formikSetField,
		formikErrors,
		formikValues,
		formikSetTouched,
		formikValidateForm,
		actionButton,
		handleAdditionalDiseaseChange,
	}) => {
		const theme = useTheme();
		const values = formikValues;
		const [isModalShow, setIsModalShow] = useState(false);
		const [blockedBannerCondition, setBlockedBannerCondition] = useState(false);
		const [showErrors, setShowErrors] = useState(false);
		const [isAllTravelersArePregnant, setAllTravelersArePregnant] =
			useState(false);
		const { classes } = useTab3Styles();
		const messages = (id) => intl.formatMessage({ id });
		const forceUpdate = utils.useForceUpdate();
		const validationObject = ValidationSchema;
		const travelerSelectionLabelStyle = {
			fontSize: theme.typography.pxToRem(13.5),
			fontWeight: theme.typography.fontWeightSemiBold,
			color: theme.palette.text.secondary,
			marginBottom: 8,
			textAlign: 'right',
		};

		const travelersWithChronicCondition = utils.filterChronicTravelers(
			values.travelers,
		);

		setTimeout(() => {
			validationCallback(validationObject);
		}, 1);

		useEffect(() => {
			setIsModalShow(
				getBlockCondition() &&
					['Wobi', 'Post_IL'].includes(agentDeveloperUtils.getAgentDeveloperName),
			);
		}, [values, blockedBannerCondition]);

		useEffect(() => {
			handleAdditionalDiseaseChange(
				values.isMedicalEscalation === 'yes' ||
					values.isMedicalTreatment === 'yes' ||
					(values.isAdditionalChronic === 'yes' &&
						(values.isSeverDisease1 === 'yes' ||
							values.isSeverDisease2 === 'yes' ||
							values.isSeverDisease3 === 'yes' ||
							values.isSeverDisease4 === 'yes')),
			);
		}, [
			values.isMedicalEscalation,
			values.isMedicalTreatment,
			values.isSeverDisease1,
			values.isSeverDisease2,
			values.isSeverDisease3,
			values.isSeverDisease4,
		]);

		useEffect(() => {
			const travelersWithChronicCondition = formikValues.travelers.filter(
				(traveler) => traveler.additionalChronic,
			);
			for (const traveler of formikValues.travelers) {
				if (
					!traveler.additionalChronic ||
					formikValues.isAdditionalChronic === 'no'
				) {
					delete traveler.severDisease1;
					delete traveler.severDisease2;
					delete traveler.severDisease3;
					delete traveler.severDisease4;
				} else if (travelersWithChronicCondition.length === 1) {
					traveler.severDisease1 = formikValues.isSeverDisease1;
					traveler.severDisease2 = formikValues.isSeverDisease2;
					traveler.severDisease3 = formikValues.isSeverDisease3;
					traveler.severDisease4 = formikValues.isSeverDisease4;
				}
			}
			if (
				(travelersWithChronicCondition.length === 0 ||
					formikValues.isAdditionalChronic === 'no') &&
				formikValues.travelers.length >= 1
			) {
				formikSetField('isSeverDisease1', '');
				formikSetField('isSeverDisease2', '');
				formikSetField('isSeverDisease3', '');
				formikSetField('isSeverDisease4', '');
			}
		}, [formikValues.isAdditionalChronic, formikValues.travelers]);

		const handleIsPregnancy = (value) => {
			formikSetField('isPregnancy', value);
			if (value === 'no') {
				const travelers = values.travelers.map((traveler, index) => {
					formikSetField(`week${index}`, '');
					return {...traveler, pregnant: false, week: ''};
				});
				formikSetField('isPregEndangered', '');
				formikSetField('pregnancyIdentifica', []);
				formikSetField('travelers', travelers);
				setAllTravelersArePregnant(false);
				return;
			}

			for (let i = 0; i < values.travelers.length; i++) {
				if (values.travelers[i].pregnant) {
					clickPregnancyIdentification(values.travelers[i], false);
				}
			}

			if (utils.filterWomenTravelers(values.travelers).length === 1) {
				const woman = utils.filterFirstWoman(values.travelers)[0];
				if (value === 'yes') {
					clickPregnancyIdentification(woman, true);
				} else if (value === 'no') {
					clickPregnancyIdentification(woman, false);
				}
			}

			setAllTravelersArePregnant(
				utils.filterPregnantTravelers(values.travelers).length ===
					values.travelers.length,
			);
		};

		const haveFertileWomen = utils.filterWomenTravelers(
			values.travelers,
		).length;
		const isPregnant = haveFertileWomen && values.isPregnancy === 'yes';

		const clickPregnancyIdentification = (woman, value) => {
			woman.pregnant = value;
			formikSetField('travelers', values.travelers);

			if (value) {
				values.pregnancyIdentifica.push(woman.ID);
			} else {
				const i = utils.getIndexFromIDArrayByID(
					values.pregnancyIdentifica,
					woman.ID,
				);
				values.pregnancyIdentifica.splice(i, 1);
			}
			formikSetField('pregnancyIdentifica', values.pregnancyIdentifica);
			updatePregnancyWeek(woman.week, woman.ID);
			setAllTravelersArePregnant(
				utils.filterPregnantTravelers(values.travelers).length ===
					values.travelers.length,
			);
		};

		const setPregnancyWeeks = (event, index, travelerID) => {
			values.travelers[index].week = event.target.value;
			formikSetField('travelers', values.travelers);
			updatePregnancyWeek(event.target.value, travelerID);
		};

		const updatePregnancyWeek = (value, travelerID) => {
			const i = utils.getIndexByID(values.travelers, travelerID);
			let newValue = value;
			if (value === undefined) {
				newValue = '';
			}
			const propertyName = `week${i}`;
			formikSetField(propertyName, newValue);
		};

		const filterTravelersWithChronicCondition = () => {
			const travelers = [...values.travelers];
			return travelers.filter((t) => t.additionalChronic);
		};

		const uncheckTravelerMinorDiseases = (index) => {
			const travelers = [...values.travelers];
			const oldDiseases = [...travelers[index].minorDiseases];
			const travelerID = travelers[index].ID;
			oldDiseases.forEach((d) => {
				const minorDiseases = [...values.minorDiseases];
				const minorDiseaseIndex = minorDiseases.findIndex((md) => md.key === d);
				const newChosenTravelers = minorDiseases[
					minorDiseaseIndex
				].chosenTravelers.filter((id) => id !== travelerID);
				handleTravelerButtonClick(
					{ currentTarget: { value: travelerID } },
					newChosenTravelers,
					minorDiseaseIndex,
				);
				if (newChosenTravelers.length === 0) {
					checkMinorDisease(minorDiseaseIndex, minorDiseases, false);
				}
			});
			if (formikValues.isAdditionalChronic === 'no') {
				travelers[index].additionalChronic = false;
			}
			formikSetField(
				'additChronIdentific',
				values.additChronIdentific.filter((id) => id !== travelerID),
			);
		};

		const handleIsChronicButtonClick = (value) => {
			formikSetField('isChronic', value);
			const newValue = value === 'yes';
			if (values.travelers.length === 1) {
				values.travelers[0].chronic = newValue;
				formikSetField('travelers', values.travelers);
			}
			if (!newValue) {
				values.travelers.forEach((trav, index) =>
					setChronicIdentification('chronic', index, false),
				);
				formikSetField('additChronIdentific', []);
				formikSetField('isAdditionalChronic', 'no');
				formikSetField('isSeverDisease1', 'no');
				formikSetField('isSeverDisease2', 'no');
				formikSetField('isSeverDisease3', 'no');
				formikSetField('isSeverDisease4', 'no');
			}
		};

		const handleIsAdditionalChronicButtonClick = (value) => {
			formikSetField('isAdditionalChronic', value);
			const newValue = value === 'yes';
			if (values.travelers.length === 1) {
				values.travelers[0].additionalChronic = newValue;
				formikSetField('travelers', values.travelers);
			}
			if (!newValue) {
				values.travelers.forEach((trav, index) =>
					setChronicIdentification('additionalChronic', index, false),
				);
				formikSetField('additChronIdentific', []);
				formikSetField('isAdditionalChronic', 'no');
				formikSetField('isSeverDisease1', 'no');
				formikSetField('isSeverDisease2', 'no');
				formikSetField('isSeverDisease3', 'no');
				formikSetField('isSeverDisease4', 'no');
			}
		};

		const setChronicIdentification = (property, index, value) => {
			/* prop is 'chronic'/'additionalChronic */
			const travelers = [...values.travelers];
			travelers[index][property] = value;
			if (
				(property === 'chronic' || property === 'additChronIdentific') &&
				!value
			) {
				uncheckTravelerMinorDiseases(index);
			} else {
				updateAdditChronIdentific(property, index, value);
			}
			formikSetField('travelers', travelers);
		};

		const checkMinorDisease = (index, array, value) => {
			array[index].checked = value;
			const travelers = [...values.travelers];

			if (travelersWithChronicCondition.length === 1) {
				const travelerIndex = travelers.findIndex((t) => t.chronic);
				if (travelerIndex > -1) {
					if (value) {
						array[index].chosenTravelers = [
							travelersWithChronicCondition[0].ID,
						];
						travelers[travelerIndex].minorDiseases.push(array[index].key);
					} else {
						const i = travelersWithChronicCondition[0].minorDiseases.findIndex(
							(d) => d.key === array[index].key,
						);
						travelers[travelerIndex].minorDiseases.splice(i, 1);
					}
					formikSetField('travelers', travelers);
					forceUpdate();
				}
			}
			formikSetField('minorDiseases', array);
			forceUpdate();
		};

		const handleTravelerButtonClick = (
			event,
			newChosenTravelers,
			diseaseIndex,
		) => {
			updateMinorDiseaseChosenTravelers(newChosenTravelers, diseaseIndex);
			updateTravelerMinorDiseases(event, newChosenTravelers, diseaseIndex);
		};

		const updateMinorDiseaseChosenTravelers = (
			newChosenTravelers,
			diseaseIndex,
		) => {
			const updatedMinorDiseases = [...values.minorDiseases];
			updatedMinorDiseases[diseaseIndex].chosenTravelers = newChosenTravelers;
			formikSetField('minorDiseases', updatedMinorDiseases);
			forceUpdate();
		};

		const updateTravelerMinorDiseases = (
			event,
			newChosenTravelers,
			diseaseIndex,
		) => {
			const travelerID = event.currentTarget.value;
			const travelerIndex = utils.getIndexByID(values.travelers, travelerID);
			const diseaseKey = values.minorDiseases[diseaseIndex].key;
			const updatedTravelers = [...values.travelers];

			if (newChosenTravelers.includes(travelerID)) {
				updatedTravelers[travelerIndex].minorDiseases.push(diseaseKey);
			} else {
				const j = updatedTravelers[travelerIndex].minorDiseases.findIndex(
					(d) => d === diseaseKey,
				);
				updatedTravelers[travelerIndex].minorDiseases.splice(j, 1);
			}

			formikSetField('travelers', updatedTravelers);
			forceUpdate();
		};

		const updateAdditChronIdentific = (property, index, value) => {
			if (!value) {
				const i = utils.getIndexFromIDArrayByID(
					values.additChronIdentific,
					values.travelers[index].ID,
				);
				values.additChronIdentific.splice(i, 1);
			} else if (
				property === 'additionalChronic' ||
				(property === 'chronic' && values.travelers[index].additionalChronic)
			) {
				values.additChronIdentific.push(values.travelers[index].ID);
			}
			formikSetField('additChronIdentific', values.additChronIdentific);
		};

		const handleSubmitStep = async () => {
			const errors = await formikValidateForm();
			const hasErrors = Object.keys(errors).some((field) =>
				Object.keys(validationObject).includes(field),
			);
			if (!hasErrors) {
				fireGtmEvent('confirmMedical', {
					medicalTravel: formikValues.isTreatmentAbroad,
					sixMonthsMedical: formikValues.isMedicalEscalation,
					medicalTests: formikValues.isMedicalTreatment,
					chronicPatient: formikValues.isChronic,
					additionalDisease: formikValues.isAdditionalChronic,
					isPregnant: formikValues.isPregnancy,
					pregnantTravelers: formikValues.travelers
						.filter((tr) => tr.pregnant)
						.map((tr) => ({
							week: tr.week,
							ID: tr.ID,
						})),
				});
				submitCallback(3);
			} else {
				setShowErrors(true);
				Object.keys(validationObject).forEach((field) =>
					formikSetTouched(field, true, false),
				);
				scrollToFirstError(errors);
			}
		};

		const someoneChronicAndPregnant = () =>
			values.travelers.some(
				(traveler) =>
					(traveler.additionalChronic ||
						traveler.medicalEscalation ||
						traveler.medicalTreatment) &&
					traveler.pregnant,
			);

		const getHarelCondition = () => {
			const policyStartDate = moment(formikValues.policyStartDate);
			const policyEndDate = moment(formikValues.policyEndDate);
			const isLessThanDays =
				policyEndDate.diff(policyStartDate, 'days') + 1 <= HAREL_MAX_DAYS;
			if (!isLessThanDays || (values.targetCountries || []).includes('USA')) {
				return (
					values.isMedicalTreatment === 'yes' ||
					values.isMedicalEscalation === 'yes' ||
					values.isSeverDisease1 === 'yes' ||
					values.isSeverDisease2 === 'yes' ||
					values.isSeverDisease3 === 'yes' ||
					values.isSeverDisease4 === 'yes'
				);
			} else {
				return (
					(values.isMedicalTreatment === 'yes' && isAllTravelersArePregnant) ||
					(values.isMedicalEscalation === 'yes' && isAllTravelersArePregnant)
				);
			}
		};
		const getBlockCondition = () =>
			values.isTreatmentAbroad === 'yes' ||
			values.isCovid19 === 'yes' ||
			getHarelCondition() ||
			(values.isPregEndangered === 'yes' &&
				utils.filterWomenTravelers(values.travelers).length) ||
			someoneChronicAndPregnant() ||
			Boolean(
				utils.filterPregnantWithBigWeek(values.travelers, values.policyEndDate)
					.length,
			);

		const manyCanBePregnant =
			isPregnant && utils.filterWomenTravelers(values.travelers).length > 1;
		const oneOrMorePregnant =
			isPregnant && utils.filterPregnantTravelers(values.travelers).length;
		const isChronicChecked = values.isChronic === 'yes';
		const isChronicAndManyTravelers =
			isChronicChecked && values.travelers.length > 1;
		const oneOrMoreHasChronic =
			isChronicChecked && utils.filterChronicTravelers(values.travelers).length;
		const manyHaveChronic =
			isChronicChecked && travelersWithChronicCondition.length > 1;
		const isAdditionalChronicChecked =
			/* isChronicChecked  && */ values.isAdditionalChronic === 'yes';
		const isAdditionalChronicAndManyTravelers =
			isAdditionalChronicChecked && values.travelers.length > 1;

		const manyCanBeTreatment =
			!isAllTravelersArePregnant && values.travelers.length > 1;
		const isBlockByCondition = getBlockCondition();

		if (isBlockByCondition !== blockedBannerCondition) {
			setBlockedBannerCondition(isBlockByCondition);
		}
		const submitButtonAriaLabel = () => {
			if (blockedBannerCondition) {
				return `${messages('continue')}, ${messages('blocked_banner.title')}`;
			} else if (formikErrors && Object.keys(formikErrors).length) {
				return `${messages('continue')}, ${messages(
					'validation.needToFillAllFields',
				)}`;
			} else {
				return null;
			}
		};

		const handleIsMedicalTreatment = (value) => {
			formikSetField('isMedicalTreatment', value);
			for (let i = 0; i < values.travelers.length; i++) {
				if (values.travelers[i].medicalTreatment) {
					setMedicalTreatmentIdentification(values.travelers[i], false);
				}
			}

			if (values.travelers.length === 1) {
				const traveler = values.travelers[0];
				if (value === 'yes') {
					setMedicalTreatmentIdentification(traveler, true);
				} else if (value === 'no') {
					setMedicalTreatmentIdentification(traveler, false);
				}
			}
		};

		const setMedicalTreatmentIdentification = (traveler, value) => {
			traveler.medicalTreatment = value;
			formikSetField('travelers', values.travelers);

			if (value) {
				values.medicalTreatmentIdentifica.push(traveler.ID);
			} else {
				const i = utils.getIndexFromIDArrayByID(
					values.medicalTreatmentIdentifica,
					traveler.ID,
				);
				values.medicalTreatmentIdentifica.splice(i, 1);
			}
			formikSetField(
				'medicalTreatmentIdentifica',
				values.medicalTreatmentIdentifica,
			);
		};

		const handleIsMedicalEscalation = (value) => {
			formikSetField('isMedicalEscalation', value);
			for (let i = 0; i < values.travelers.length; i++) {
				if (values.travelers[i].medicalEscalation) {
					setMedicalEscalationldentification(values.travelers[i], false);
				}
			}

			if (values.travelers.length === 1) {
				const traveler = values.travelers[0];
				if (value === 'yes') {
					setMedicalEscalationldentification(traveler, true);
				} else if (value === 'no') {
					setMedicalEscalationldentification(traveler, false);
				}
			}
		};

		const setMedicalEscalationldentification = (traveler, value) => {
			traveler.medicalEscalation = value;
			formikSetField('travelers', values.travelers);

			if (value) {
				values.medicalEscalationIdentifica.push(traveler.ID);
			} else {
				const i = utils.getIndexFromIDArrayByID(
					values.medicalEscalationIdentifica,
					traveler.ID,
				);
				values.medicalEscalationIdentifica.splice(i, 1);
			}
			formikSetField(
				'medicalEscalationIdentifica',
				values.medicalEscalationIdentifica,
			);
		};

		const handleIsSeverDisease = (property, travelerProperty, value) => {
			formikSetField(property, value);
			const filtertravelers = filterTravelersWithChronicCondition();
			for (let i = 0; i < filtertravelers.length; i++) {
				if (values.travelers[i][travelerProperty]) {
					setSeverDiseaseldentification(
						travelerProperty,
						values.travelers[i],
						false,
					);
				}
			}
			if (filtertravelers.length === 1) {
				const traveler = filtertravelers[0];
				if (value === 'yes') {
					setSeverDiseaseldentification(travelerProperty, traveler, true);
				} else if (value === 'no') {
					setSeverDiseaseldentification(travelerProperty, traveler, false);
				}
			}
		};

		const setSeverDiseaseldentification = (property, traveler, value) => {
			// const travelers = [...values.travelers];
			// eslint-disable-next-line no-param-reassign
			value = value ? 'yes' : 'no';
			traveler[property] = value;
			formikSetField('travelers', values.travelers);

			if (value === 'yes') {
				values[property + 'Identifica'].push(traveler.ID);
			} else {
				const i = utils.getIndexFromIDArrayByID(
					values[property + 'Identifica'],
					traveler.ID,
				);
				values[property + 'Identifica'].splice(i, 1);
			}
			formikSetField(property + 'Identifica', values[property + 'Identifica']);
		};

		const manyCanBeSeverDisease =
			filterTravelersWithChronicCondition().length > 1;
		return (
			<div
				className={`${hideTabClassName} travel-medical-conditions`}
				id={idForAccessibility}
				role='tabpanel'
			>
				<MedicalPageLayout
					title={messages('medical.heading')}
					subtitle={messages('medical.subtitle')}
					footer={
						<FlowFooter
							onBack={() => submitCallback(1)}
							onNext={handleSubmitStep}
							nextDisabled={formikIsSubmitting || blockedBannerCondition}
							primaryButtonProps={{'data-testid': TEST_IDS.flow.continueMedical}}
						/>
					}
				>
						{values.targetCountries.includes('USA') && (
							<MedicalQuestion
								icon='heart'
								text={messages('step1.tab3.isUSAMedicalCondition')}
								value={values.isUSAMedicalCondition}
								onChange={(v) => formikSetField('isUSAMedicalCondition', v)}
							error={showErrors && Boolean(formikErrors.isUSAMedicalCondition)}
								dataTestId='is-vaccinated'
							/>
						)}

						<MedicalQuestion
							icon='plane'
							text={messages('step1.tab3.isTreatmentAbroad')}
							value={values.isTreatmentAbroad}
							onChange={(v) => {
								fireGtmEvent('MedicalTravel', { value: v });
								formikSetField('isTreatmentAbroad', v);
							}}
						error={showErrors && Boolean(formikErrors.isTreatmentAbroad)}
							dataTestId='is-treatment-abroad'
						/>

						<MedicalQuestion
							icon='doc'
							text={messages('step1.tab3.isMedicalTreatment')}
							detail={intl.formatMessage({id: 'step1.tab3.isMedicalTreatmentSmallText'}, {notation: (chunks) => chunks})}
							detailLabel={intl.formatMessage({id: 'step1.tab3.superExtand'})}
							value={values.isMedicalTreatment}
							onChange={(v) => {
								fireGtmEvent('MedicalTest', { value: v });
								handleIsMedicalTreatment(v);
							}}
						error={showErrors && Boolean(formikErrors.isMedicalTreatment)}
							dataTestId='is-medical-treatment'
						>
							{manyCanBeTreatment && values.isMedicalTreatment === 'yes' && (
								<div style={{marginTop: 12}}>
									<div style={travelerSelectionLabelStyle}>
										{messages('step1.tab3.select_travelers')}
									</div>
									<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
										{values.travelers.map((traveler, i) => (
											<Checkbox
												key={`traveler-${i}-medical-treatment`}
												data-testid={`traveler-${i + 1}-medical-treatment`}
												checked={Boolean(traveler.medicalTreatment)}
												onChange={() => setMedicalTreatmentIdentification(traveler, !traveler.medicalTreatment)}
												label={traveler.fullName}
											/>
										))}
									</div>
									{showErrors && (
										<FormHelperText error>
											<ErrorMessage name='medicalTreatmentIdentifica' />
										</FormHelperText>
									)}
								</div>
							)}
						</MedicalQuestion>

						<MedicalQuestion
							icon='stethoscope'
							text={messages('step1.tab3.isMedicalEscalation')}
							detail={intl.formatMessage({id: 'step1.tab3.isMedicalEscalation1'}, {notation: (chunks) => chunks})}
							detailLabel={intl.formatMessage({id: 'step1.tab3.extand'})}
							value={values.isMedicalEscalation}
							onChange={(v) => {
								fireGtmEvent('SixMonthMedical', { value: v });
								handleIsMedicalEscalation(v);
							}}
						error={showErrors && Boolean(formikErrors.isMedicalEscalation)}
							dataTestId='is-medical-escalation'
						>
							{manyCanBeTreatment && values.isMedicalEscalation === 'yes' && (
								<div style={{marginTop: 12}}>
									<div style={travelerSelectionLabelStyle}>
										{messages('step1.tab3.select_travelers')}
									</div>
									<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
										{values.travelers.map((traveler, i) => (
											<Checkbox
												key={`traveler-${i}-medical-escalation`}
												data-testid={`traveler-${i + 1}-medical-escalation`}
												checked={Boolean(traveler.medicalEscalation)}
												onChange={() => setMedicalEscalationldentification(traveler, !traveler.medicalEscalation)}
												label={traveler.fullName}
											/>
										))}
									</div>
									{showErrors && (
										<FormHelperText error>
											<ErrorMessage name='medicalEscalationIdentifica' />
										</FormHelperText>
									)}
								</div>
							)}
						</MedicalQuestion>

						{/* Pregnancy — shown if fertile women exist */}
						{haveFertileWomen ? (
							<MedicalQuestion
								icon='users'
								text={messages('step1.tab3.isPregnancy')}
								value={values.isPregnancy}
								onChange={(v) => {
									fireGtmEvent('Pregnancy', { value: v });
									handleIsPregnancy(v);
								}}
								error={showErrors && Boolean(formikErrors.isPregnancy)}
								dataTestId='is-pregnancy'
							>
								{manyCanBePregnant && (
									<div style={{marginTop: 12}}>
										<div style={travelerSelectionLabelStyle}>
											{messages('step1.tab3.pregnancyIdentification')}
										</div>
										<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
											{values.travelers.map((traveler, i) =>
												traveler.gender === '1' ? null : (
													<Checkbox
														key={`traveler-${i}-pregnancy`}
														data-testid={`traveler-${i + 1}-pregnancy`}
														checked={Boolean(traveler.pregnant)}
														onChange={() => clickPregnancyIdentification(traveler, !traveler.pregnant)}
														label={traveler.fullName}
													/>
												),
											)}
										</div>
										{showErrors && (
											<FormHelperText error>
												<ErrorMessage name='pregnancyIdentifica' />
											</FormHelperText>
										)}
									</div>
								)}
								{isPregnant && (
									<MedicalSubQuestion
										text={messages('step1.tab3.isPregEndangered')}
										value={values.isPregEndangered}
										onChange={(v) => {
											fireGtmEvent('pregnancyRisk', { value: v });
											formikSetField('isPregEndangered', v);
										}}
										error={showErrors && Boolean(formikErrors.isPregEndangered)}
										dataTestId='is-preg-endangered'
									/>
								)}
								{oneOrMorePregnant ? (
									<div style={{marginTop: 12}}>
										<div style={travelerSelectionLabelStyle}>
											{messages('step1.tab3.pregnancyWeek')}
											<span style={{fontSize: theme.typography.pxToRem(12), color: theme.palette.text.secondary, marginInlineStart: 4}}>
												{messages('step1.tab3.pregnancyWeek_notation')}
											</span>
										</div>
										{values.travelers.map((traveler, i) =>
											!traveler.pregnant ? null : (
												<div key={`traveler-${i + 1}-pregnancyWeek`} style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8}}>
													<span style={{fontSize: theme.typography.pxToRem(14), fontWeight: theme.typography.fontWeightSemiBold, color: theme.palette.text.primary, minWidth: 80}}>{traveler.fullName}</span>
													<TextField
														data-testid={`pregnancy-week${i + 1}`}
														value={traveler.week || ''}
														name={`week${i}`}
														size='small'
														error={showErrors && Boolean(formikErrors[`week${i}`])}
														onChange={(event) => setPregnancyWeeks(event, i, traveler.ID)}
														style={{maxWidth: 80}}
													/>
													{showErrors && (
														<FormHelperText error>
															<ErrorMessage name={`week${i}`} />
														</FormHelperText>
													)}
												</div>
											),
										)}
									</div>
								) : null}
							</MedicalQuestion>
						) : null}

						{/* isAdditionalChronic — medication/chronic question */}
						<MedicalQuestion
							icon='pill'
							text={messages('step1.tab3.isAdditionalChronic')}
							detail={intl.formatMessage({id: 'step1.tab3.isAdditionalChronicSmallText'}, {notation: (chunks) => chunks})}
							detailLabel={intl.formatMessage({id: 'step1.tab3.extand'})}
							value={values.isAdditionalChronic}
							onChange={(v) => {
								fireGtmEvent('ChronicPatient', { value: v });
								handleIsAdditionalChronicButtonClick(v);
							}}
						error={showErrors && !isAdditionalChronicChecked && Boolean(formikErrors.isAdditionalChronic)}
							dataTestId='is-additional-chronic'
							last
						>
							{isAdditionalChronicAndManyTravelers && (
								<div style={{marginTop: 12}}>
									<div style={travelerSelectionLabelStyle}>
										{messages('step1.tab3.chronicIdentification')}
									</div>
									<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
										{values.travelers.map((traveler, i) => (
											<Checkbox
												key={`traveler-${i}-additionalCh`}
												data-testid={`traveler-${i + 1}-additionalCh`}
												checked={Boolean(traveler.additionalChronic)}
												onChange={() => setChronicIdentification('additionalChronic', i, !traveler.additionalChronic)}
												label={traveler.fullName}
											/>
										))}
									</div>
									{showErrors && (
										<FormHelperText error>
											<ErrorMessage name='additChronIdentific' />
										</FormHelperText>
									)}
								</div>
							)}
						</MedicalQuestion>

						{/* Severe disease sub-questions — shown when isAdditionalChronic=yes */}
						{isAdditionalChronicChecked && filterTravelersWithChronicCondition().length > 0 && (
							<div className='medical-followup'>
								<div className='medical-followup-title'>
									{messages('step1.tab3.severDisease_title')}
								</div>
								<MedicalSubQuestion
									text={messages('step1.tab3.isSeverDisease1')}
									value={values.isSeverDisease1}
									onChange={(v) => handleIsSeverDisease('isSeverDisease1', 'severDisease1', v)}
							error={showErrors && Boolean(formikErrors.isSeverDisease1)}
									dataTestId='is-sever-disease-1'
								/>
								{manyCanBeSeverDisease && values.isSeverDisease1 === 'yes' && (
									<div style={{paddingInlineStart: 16, marginBottom: 8}}>
										<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
											{utils.filterAllNotPregnantTravelers(filterTravelersWithChronicCondition()).map((traveler, i) => (
												<Checkbox
													key={`traveler-${i}-sever-disease-1`}
													data-testid={`traveler-${i + 1}-sever-disease-1`}
													checked={traveler.severDisease1 === 'yes'}
													onChange={() => setSeverDiseaseldentification('severDisease1', traveler, traveler.severDisease1 !== 'yes')}
													label={traveler.fullName}
												/>
											))}
										</div>
										{showErrors && <FormHelperText error><ErrorMessage name='severDisease1Identifica' /></FormHelperText>}
									</div>
								)}
								<MedicalSubQuestion
									text={messages('step1.tab3.isSeverDisease2')}
									value={values.isSeverDisease2}
									onChange={(v) => handleIsSeverDisease('isSeverDisease2', 'severDisease2', v)}
							error={showErrors && Boolean(formikErrors.isSeverDisease2)}
									dataTestId='is-sever-disease-2'
								/>
								{manyCanBeSeverDisease && values.isSeverDisease2 === 'yes' && (
									<div style={{paddingInlineStart: 16, marginBottom: 8}}>
										<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
											{utils.filterAllNotPregnantTravelers(filterTravelersWithChronicCondition()).map((traveler, i) => (
												<Checkbox
													key={`traveler-${i}-sever-disease-2`}
													data-testid={`traveler-${i + 1}-sever-disease-2`}
													checked={traveler.severDisease2 === 'yes'}
													onChange={() => setSeverDiseaseldentification('severDisease2', traveler, traveler.severDisease2 !== 'yes')}
													label={traveler.fullName}
												/>
											))}
										</div>
										{showErrors && <FormHelperText error><ErrorMessage name='severDisease2Identifica' /></FormHelperText>}
									</div>
								)}
								<MedicalSubQuestion
									text={messages('step1.tab3.isSeverDisease3')}
									value={values.isSeverDisease3}
									onChange={(v) => handleIsSeverDisease('isSeverDisease3', 'severDisease3', v)}
							error={showErrors && Boolean(formikErrors.isSeverDisease3)}
									dataTestId='is-sever-disease-3'
								/>
								{manyCanBeSeverDisease && values.isSeverDisease3 === 'yes' && (
									<div style={{paddingInlineStart: 16, marginBottom: 8}}>
										<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
											{utils.filterAllNotPregnantTravelers(filterTravelersWithChronicCondition()).map((traveler, i) => (
												<Checkbox
													key={`traveler-${i}-sever-disease-3`}
													data-testid={`traveler-${i + 1}-sever-disease-3`}
													checked={traveler.severDisease3 === 'yes'}
													onChange={() => setSeverDiseaseldentification('severDisease3', traveler, traveler.severDisease3 !== 'yes')}
													label={traveler.fullName}
												/>
											))}
										</div>
										{showErrors && <FormHelperText error><ErrorMessage name='severDisease3Identifica' /></FormHelperText>}
									</div>
								)}
								<MedicalSubQuestion
									text={messages('step1.tab3.isSeverDisease4')}
									value={values.isSeverDisease4}
									onChange={(v) => handleIsSeverDisease('isSeverDisease4', 'severDisease4', v)}
							error={showErrors && Boolean(formikErrors.isSeverDisease4)}
									dataTestId='is-sever-disease-4'
									last
								/>
								{manyCanBeSeverDisease && values.isSeverDisease4 === 'yes' && (
									<div style={{paddingInlineStart: 16, marginBottom: 8}}>
										<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
											{utils.filterAllNotPregnantTravelers(filterTravelersWithChronicCondition()).map((traveler, i) => (
												<Checkbox
													key={`traveler-${i}-sever-disease-4`}
													data-testid={`traveler-${i + 1}-sever-disease-4`}
													checked={traveler.severDisease4 === 'yes'}
													onChange={() => setSeverDiseaseldentification('severDisease4', traveler, traveler.severDisease4 !== 'yes')}
													label={traveler.fullName}
												/>
											))}
										</div>
										{showErrors && <FormHelperText error><ErrorMessage name='severDisease4Identifica' /></FormHelperText>}
									</div>
								)}
							</div>
						)}

				</MedicalPageLayout>
				<UnsupportedMedicalCases
					values={values}
					isModalShow={!hideTabClassName && isModalShow}
					hideContactPassportCard={someoneChronicAndPregnant()}
					close={() => setIsModalShow(false)}
				/>
			</div>
		);
	},
);

Tab3.propTypes = {
	className: PropTypes.string,
	idForAccessibility: PropTypes.string,
	intl: PropTypes.object,
	submitCallback: PropTypes.func,
	formikIsSubmitting: PropTypes.bool,
	validationCallback: PropTypes.func,
	formikSetField: PropTypes.func,
	formikErrors: PropTypes.object,
	formikValues: PropTypes.object,
	formikSetTouched: PropTypes.func,
	formikValidateForm: PropTypes.func,
	actionButton: PropTypes.object,
	handleAdditionalDiseaseChange: PropTypes.func,
};

export default injectIntl(Tab3);
